/**
 * Utilità per le pagine per città (/noleggio-auto/:citta, /noleggio-moto/:citta).
 *
 * Scritto in CommonJS di proposito: lo usano sia l'app React (import) sia lo
 * script Node scripts/prerender.js (require), così slug e soglia restano
 * identici nei due posti.
 *
 * Fonte dati: il campo "comune" di /directoryIndex (città dell'azienda, cioè
 * clienti reali). Le città si raggruppano ignorando maiuscole, accenti e
 * suffissi come "(MI)" o ", MI".
 */

// Sotto questa soglia la pagina città esiste ma è noindex e non finisce in
// sitemap/prerender: una pagina con un solo noleggio è contenuto sottile.
const CITY_MIN_COMPANIES = 2;

const SMALL_WORDS = new Set(['di', 'del', 'della', 'dei', 'delle', 'sul', 'sulla', 'in', 'nel', 'nella', 'e', 'al', 'alla']);

/** "Milano (MI)" / "Milano, MI" / "  milano " → "Milano" */
function cleanCityName(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/\(.*?\)/g, ' ')
    .split(',')[0]
    .replace(/\s+/g, ' ')
    .trim();
}

/** Slug per URL: minuscolo, senza accenti né apostrofi. "L'Aquila" → "laquila" */
function slugifyCity(raw) {
  return cleanCityName(raw)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Se il nome è tutto minuscolo/maiuscolo lo porta in "Title Case all'italiana". */
function prettyCityName(name) {
  if (!name) return '';
  if (name !== name.toLowerCase() && name !== name.toUpperCase()) return name;
  const words = name.toLowerCase().split(' ').map((w, i) => {
    if (i > 0 && SMALL_WORDS.has(w)) return w;
    return w.replace(/(^|['’-])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase());
  });
  return words.join(' ');
}

/** "reggio-emilia" → "Reggio Emilia" (solo come ripiego mentre i dati caricano). */
function humanizeSlug(slug) {
  return prettyCityName((slug || '').replace(/-/g, ' '));
}

function offersMode(entry, mode) {
  if (mode === 'auto') return entry.mostraAuto !== false;
  if (mode === 'moto') return entry.mostraMoto !== false;
  return true;
}

/**
 * Elenco città per un tipo di veicolo ('auto' | 'moto').
 * `entries`: array di oggetti con slug + (citta | comune) + mostraAuto/mostraMoto
 * (vale sia per i record grezzi di /directoryIndex sia per quelli già mappati
 * dalla pagina). Ritorna [{ slug, name, count }] ordinato per numero di noleggi.
 */
function buildCityIndex(entries, mode) {
  const bySlug = new Map();
  (entries || []).forEach(e => {
    if (!e || !e.slug || !offersMode(e, mode)) return;
    const cleaned = cleanCityName(e.citta != null ? e.citta : e.comune);
    const slug = slugifyCity(cleaned);
    if (!slug) return;
    const item = bySlug.get(slug) || { slug, count: 0, forms: new Map() };
    item.count += 1;
    item.forms.set(cleaned, (item.forms.get(cleaned) || 0) + 1);
    bySlug.set(slug, item);
  });
  return [...bySlug.values()]
    .map(({ slug, count, forms }) => {
      const best = [...forms.entries()].sort((a, b) => b[1] - a[1])[0][0];
      return { slug, count, name: prettyCityName(best) };
    })
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'it'));
}

/** Solo le città abbastanza "piene" da meritare indicizzazione. */
function indexableCities(entries, mode) {
  return buildCityIndex(entries, mode).filter(c => c.count >= CITY_MIN_COMPANIES);
}

const MODE_PATH = { auto: '/noleggio-auto', moto: '/noleggio-moto' };

module.exports = {
  CITY_MIN_COMPANIES,
  MODE_PATH,
  cleanCityName,
  slugifyCity,
  prettyCityName,
  humanizeSlug,
  buildCityIndex,
  indexableCities,
};
